import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  NotificacaoRecipientView,
  RecipientFilters,
  RecipientPagination,
} from '../../types/recipient.types';

interface RecipientState {
  notificacoes: NotificacaoRecipientView[];
  notificacaoAtual: NotificacaoRecipientView | null;
  isLoading: boolean;
  error: string | null;
  filters: RecipientFilters;
  pagination: RecipientPagination;
}

const initialState: RecipientState = {
  notificacoes: [],
  notificacaoAtual: null,
  isLoading: false,
  error: null,
  filters: {
    status: 'todas',
    page: 1,
    limit: 10,
  },
  pagination: {
    page: 1,
    total: 0,
    limit: 10,
    hasMore: false,
  },
};

const recipientSlice = createSlice({
  name: 'recipient',
  initialState,
  reducers: {
    setNotificacoes: (state, action: PayloadAction<NotificacaoRecipientView[]>) => {
      state.notificacoes = action.payload;
    },
    appendNotificacoes: (state, action: PayloadAction<NotificacaoRecipientView[]>) => {
      state.notificacoes = [...state.notificacoes, ...action.payload];
    },
    setNotificacaoAtual: (state, action: PayloadAction<NotificacaoRecipientView | null>) => {
      state.notificacaoAtual = action.payload;
    },
    updateNotificacao: (
      state,
      action: PayloadAction<Partial<NotificacaoRecipientView> & { id: string }>,
    ) => {
      const index = state.notificacoes.findIndex((n) => n.id === action.payload.id);
      if (index !== -1) {
        state.notificacoes[index] = { ...state.notificacoes[index], ...action.payload };
      }
      if (state.notificacaoAtual?.id === action.payload.id) {
        state.notificacaoAtual = { ...state.notificacaoAtual, ...action.payload };
      }
    },
    setFilters: (state, action: PayloadAction<RecipientFilters>) => {
      state.filters = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPagination: (state, action: PayloadAction<RecipientPagination>) => {
      state.pagination = action.payload;
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notificacao = state.notificacoes.find((n) => n.id === action.payload);
      if (notificacao) {
        notificacao.foiLida = true;
        notificacao.dataLeitura = new Date().toISOString();
      }
      if (state.notificacaoAtual?.id === action.payload) {
        state.notificacaoAtual.foiLida = true;
        state.notificacaoAtual.dataLeitura = new Date().toISOString();
      }
    },
    setComprovanteEnviado: (state, action: PayloadAction<string>) => {
      const notificacao = state.notificacoes.find((n) => n.id === action.payload);
      if (notificacao) {
        notificacao.comprovanteEnviado = true;
      }
      if (state.notificacaoAtual?.id === action.payload) {
        state.notificacaoAtual.comprovanteEnviado = true;
      }
    },
    setNegociacaoAberta: (state, action: PayloadAction<string>) => {
      const notificacao = state.notificacoes.find((n) => n.id === action.payload);
      if (notificacao) {
        notificacao.negociacaoAberta = true;
      }
      if (state.notificacaoAtual?.id === action.payload) {
        state.notificacaoAtual.negociacaoAberta = true;
      }
    },
  },
});

export const {
  setNotificacoes,
  appendNotificacoes,
  setNotificacaoAtual,
  setFilters,
  setLoading,
  setError,
  setPagination,
  markAsRead,
  setComprovanteEnviado,
  setNegociacaoAberta,
} = recipientSlice.actions;

export default recipientSlice.reducer;
